import { defineMessages } from '@formatjs/intl';
import fetchJsonp from 'fetch-jsonp';
import { Map } from 'immutable';
import loglevel from 'loglevel';
import noop from 'lodash.noop';
import { createSelector } from 'reselect';
import { v1 } from 'uuid';

var PrintingMethod;
(function (PrintingMethod) {
    PrintingMethod["PDF"] = "PDF";
    PrintingMethod["BOCA"] = "BOCA";
    PrintingMethod["FGL"] = "FGL";
})(PrintingMethod || (PrintingMethod = {}));

var PrintingDpi;
(function (PrintingDpi) {
    PrintingDpi["Dpi300"] = "DPI300";
    PrintingDpi["Dpi200"] = "DPI200";
})(PrintingDpi || (PrintingDpi = {}));

const tmdsFragmentMessages = defineMessages({
    suggestUsingSMS: {
        id: 'tmds.tryUsingSMS',
        defaultMessage: 'Try using SMS Delivery instead.',
    },
    suggestConnection: {
        id: 'tmds.suggestConnection',
        defaultMessage: 'Check the printer connection.',
    },
    addPaper: {
        id: 'tmds.addPaper',
        defaultMessage: 'Add more paper.',
    },
    removeJam: {
        id: 'tmds.removeJam',
        defaultMessage: 'Remove the jammed paper.',
    },
    checkPort: {
        id: 'tmds.checkPort',
        defaultMessage: 'Check hardware port.',
    },
    checkPrinter: {
        id: 'tmds.checkPrinter',
        defaultMessage: 'Check the printer.',
    },
    printer: {
        id: 'tmds.printer',
        defaultMessage: 'Printer',
    },
});
const values = {
    suggestion: Object.assign({}, tmdsFragmentMessages.suggestUsingSMS),
    printer: Object.assign({}, tmdsFragmentMessages.printer),
};
const tmdsMessages = defineMessages({
    goDirection: {
        id: 'tmds.goDirection',
        defaultMessage: 'Then go to the order page and try printing again.',
        values: {},
    },
    extendedMessage: {
        id: 'tmds.extendedMessage',
        defaultMessage: '{suggestion} {direction}',
    },
    onlineNotice: {
        id: 'tmds.onlineNotice',
        defaultMessage: '{printer} is now online.',
        values,
    },
    offlineNotice: {
        id: 'tmds.offlineNotice',
        defaultMessage: '{printer} is now offline.',
        values,
    },
    // TMDS error notification messsages
    offlineError: {
        id: 'tmds.offlineError',
        defaultMessage: '{printer} is offline. {suggestion}',
        values,
    },
    outOfPaperError: {
        id: 'tmds.outOfPaperError',
        defaultMessage: '{printer} is out of paper.',
        values,
    },
    paperJamError: {
        id: 'tmds.paperJamError',
        defaultMessage: '{printer} has a paper jam.',
        values,
    },
    paperOutOrJam: {
        id: 'tmds.paperOutOrJam',
        defaultMessage: '{printer} is jammed or out of paper.',
        values,
    },
    cutterJam: {
        id: 'tmds.cutterJam',
        defaultMessage: '{printer} cutter is jammed.',
        values,
    },
    portAccessError: {
        id: 'tmds.portAccessError',
        defaultMessage: 'Hardware port cannot be accessed.',
    },
    portIsUsed: {
        id: 'tmds.portIsUsed',
        defaultMessage: 'Hardware port is already in use.',
    },
    portNotExist: {
        id: 'tmds.portNotExist',
        defaultMessage: 'Hardware port does not exist.',
    },
    printerNoResponse: {
        id: 'tmds.printerNoResponse',
        defaultMessage: '{printer} is not responding.',
        values,
    },
    removed: {
        id: 'tmds.removed',
        defaultMessage: '{printer} is no longer available.',
        values,
    },
    stopped: {
        id: 'tmds.stopped',
        defaultMessage: 'TMDS is not responding. Close TMWin and try again or restart TMDS.',
    },
    unknownError: {
        id: 'tmds.unknownError',
        defaultMessage: 'There was an error.',
    },
    noAvailable: {
        id: 'tmds.noAvailable',
        defaultMessage: 'No printers available.',
    },
});

var TmdsPriority;
(function (TmdsPriority) {
    TmdsPriority["High"] = "High";
    TmdsPriority["Normal"] = "Normal";
    TmdsPriority["Low"] = "Low";
})(TmdsPriority || (TmdsPriority = {}));
var TmdsMethod;
(function (TmdsMethod) {
    TmdsMethod["DeviceStatus"] = "deviceStatus";
    TmdsMethod["GetPrintingStatus"] = "getPrintingStatus";
    TmdsMethod["Print"] = "print";
    TmdsMethod["Reset"] = "reset";
    TmdsMethod["StartTransaction"] = "starttransaction";
    TmdsMethod["StopTransaction"] = "stoptransaction";
    TmdsMethod["AckTransaction"] = "acktransaction";
    TmdsMethod["NackTransaction"] = "naktransaction";
    TmdsMethod["TestConnectivity"] = "testconnectivity";
})(TmdsMethod || (TmdsMethod = {}));
const generateTmdsMessage = (options = {}) => {
    const tmdsMessage = {
        ccpmessage: Object.assign({ version: '1.0.0', priority: TmdsPriority.Normal, caller: 'SDR', requestid: '', resource: '//localmachine/HardwareService', resourceid: '', method: TmdsMethod.Print, data: {} }, options),
    };
    return JSON.stringify(tmdsMessage);
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
}

const TMDS_HOST = 'localhost:43210/';
const TMDS_HTTPS_BASE_URL = `https://${TMDS_HOST}`;
const TMDS_SEND_MESSAGE_URL = `${TMDS_HTTPS_BASE_URL}sendmessage.aspx`;
const TMDS_DEFAULT_CHANNEL_URL = `${TMDS_HTTPS_BASE_URL}DefaultChannel.ashx`;

var PrinterStatus;
(function (PrinterStatus) {
    PrinterStatus["OfflineNotice"] = "offline_notice";
    PrinterStatus["OnlineNotice"] = "online_notice";
    PrinterStatus["Offline"] = "offline";
    PrinterStatus["Done"] = "done";
    PrinterStatus["Error"] = "error";
    PrinterStatus["Ready"] = "ready";
    PrinterStatus["Removed"] = "removed";
    PrinterStatus["Paused"] = "paused";
    PrinterStatus["Stopped"] = "stopped";
    PrinterStatus["Success"] = "success";
    PrinterStatus["Printing"] = "printing";
    PrinterStatus["PaperOut"] = "paper_out";
    PrinterStatus["OutOfPaper"] = "out_of_paper";
    PrinterStatus["PaperJam"] = "paper_jam";
    PrinterStatus["PortAccessError"] = "port_access_error";
    PrinterStatus["PortNotExist"] = "port_not_exist";
    PrinterStatus["CutterJam"] = "cutter_jam";
    PrinterStatus["PaperOutOrJam"] = "paper_out_or_jam";
    PrinterStatus["WrongRequestId"] = "wrong_request_id";
    PrinterStatus["NotAvailable"] = "not_available";
})(PrinterStatus || (PrinterStatus = {}));
const DisabledPrinterStatus = [
    PrinterStatus.Offline,
    PrinterStatus.Stopped,
    PrinterStatus.Removed,
    PrinterStatus.PortAccessError,
    PrinterStatus.PortNotExist,
    PrinterStatus.NotAvailable,
];
const JobCompleteStatus = [PrinterStatus.Done, PrinterStatus.Ready, PrinterStatus.Success];

const urlencode = (str) => escape(str)
    .replace(/\+/g, '%2B')
    .replace(/%20/g, '+')
    .replace(/\*/g, '%2A')
    .replace(/\//g, '%2F')
    .replace(/@/g, '%40');

const delay = (millis) => {
    return new Promise((resolve) => {
        setTimeout(() => resolve(true), millis);
    });
};

var _TmdsClientConfiguration_cloudLogger, _TmdsClientConfiguration_printError;
const instance$1 = Symbol();
class TmdsClientConfiguration {
    constructor() {
        this.defaultLogLevel = 'silent';
        this.defaultCallback = noop;
        _TmdsClientConfiguration_cloudLogger.set(this, void 0);
        _TmdsClientConfiguration_printError.set(this, void 0);
        loglevel.setLevel(this.defaultLogLevel);
        __classPrivateFieldSet(this, _TmdsClientConfiguration_cloudLogger, this.defaultCallback, "f");
        __classPrivateFieldSet(this, _TmdsClientConfiguration_printError, this.defaultCallback, "f");
    }
    static get instance() {
        if (!TmdsClientConfiguration[instance$1]) {
            TmdsClientConfiguration[instance$1] = new TmdsClientConfiguration();
        }
        return TmdsClientConfiguration[instance$1];
    }
    cloudLogger(log) {
        __classPrivateFieldGet(this, _TmdsClientConfiguration_cloudLogger, "f").call(this, log);
    }
    printError(error) {
        __classPrivateFieldGet(this, _TmdsClientConfiguration_printError, "f").call(this, error);
    }
    applyGlobalConfiguration(configuration) {
        loglevel.setLevel(configuration.printLogs || this.defaultLogLevel);
        __classPrivateFieldSet(this, _TmdsClientConfiguration_cloudLogger, configuration.cloudLogger || this.defaultCallback, "f");
        __classPrivateFieldSet(this, _TmdsClientConfiguration_printError, configuration.printErrorCallback || this.defaultCallback, "f");
    }
}
_TmdsClientConfiguration_cloudLogger = new WeakMap(), _TmdsClientConfiguration_printError = new WeakMap();
var TmdsClientConfiguration$1 = TmdsClientConfiguration.instance;

/* eslint-disable @typescript-eslint/no-explicit-any */
const trace = (...msg) => loglevel.trace(msg);
const debug = (...msg) => loglevel.debug(msg);
const info = (...msg) => loglevel.info(msg);
const warn = (...msg) => loglevel.warn(msg);
const error = (...msg) => loglevel.error(msg);
const logClientToCloud = (message) => TmdsClientConfiguration$1.cloudLogger(message);
var logger = {
    trace,
    debug,
    info,
    warn,
    error,
    logClientToCloud,
};

const stateSelector = (state) => state;
const selectPrinterList = createSelector(stateSelector, (state) => state.get('tmdsPrinters') || []);
const selectSelectedPrinter = createSelector(stateSelector, selectPrinterList, (state, printerList) => {
    const selectedPrinter = state.get('selectedPrinter');
    return selectedPrinter || (printerList.length === 1 ? printerList[0] : null);
});
const selectSelectedPrinterData = createSelector(selectPrinterList, selectSelectedPrinter, (printers, selected) => {
    if (!selected) {
        return null;
    }
    return printers.find((printer) => printer.id === selected.id) || null;
});
const selectHasPaymentDevice = createSelector(stateSelector, (state) => !!state.get('paymentDevice'));
const selectLocalPrinterError = createSelector(stateSelector, (state) => state.get('localPrinterError'));
const selectShowEmvCancel = createSelector(stateSelector, (state) => state.get('showEmvCancel'));
const selectIsLoadingCheckout = createSelector(stateSelector, (state) => state.get('isLoadingCheckout'));
const selectNakTransactionResult = createSelector(stateSelector, (state) => state.get('nakTransactionResult'));
const selectPaymentResult = createSelector(stateSelector, (state) => state.get('paymentResult'));

const initialState = {
    tmdsPrinters: [],
    paymentDevice: null,
    selectedPrinter: null,
    localPrinterError: null,
    showEmvCancel: false,
    isLoadingCheckout: false,
    paymentResult: null,
    nakTransactionResult: {
        success: true,
    },
};
let state;
const toImmutableState = (mutableState) => {
    return Map(mutableState);
};
const tmdsSetDevices = (currentState, value) => {
    const devices = value instanceof Array ? value : [value];
    const tmdsPrinters = devices
        .filter((item) => item !== null)
        .filter((item) => item.type === "printdevice" /* TmdsDeviceType.Print */)
        .map(({ installedid, friendlyname, accepted_formats, status, printerDPI }) => {
        const printerStatus = String(status).toLowerCase();
        return {
            id: installedid,
            name: friendlyname,
            method: accepted_formats === PrintingMethod.FGL ? PrintingMethod.BOCA : PrintingMethod.PDF,
            hasError: DisabledPrinterStatus.indexOf(printerStatus) > -1,
            status: printerStatus,
            dpi: parseInt(printerDPI, 10) === 200 ? PrintingDpi.Dpi200 : PrintingDpi.Dpi300,
            label: friendlyname,
        };
    });
    const paymentDevice = devices
        .filter((item) => item !== null)
        .filter((item) => item.type === "paymentdevice" /* TmdsDeviceType.Payment */)
        .map(({ installedid, friendlyname, status }) => {
        const printerStatus = String(status).toLowerCase();
        return {
            id: installedid,
            name: friendlyname,
            status: printerStatus,
        };
    })
        .shift() || null;
    return currentState.set('tmdsPrinters', tmdsPrinters).set('paymentDevice', paymentDevice);
};
const clearTmdsPrinterErrorNotification = (currentState) => currentState.set('localPrinterError', null);
const selectPrinter = (currentState, value) => {
    const printers = selectPrinterList(getState());
    // Get the real printer data from the label as the front might not send the real object
    // Fallback with the sent value
    const newPrinter = printers.find((printer) => printer.label === (value === null || value === void 0 ? void 0 : value.label)) || value;
    logger.debug('Selected printer', newPrinter);
    return currentState.set('selectedPrinter', newPrinter).set('localPrinterError', null);
};
const clearPrinterData = (currentState) => currentState.set('selectedPrinter', null).set('tmdsPrinters', []);
const tmdsPrinterErrorNotification = (currentState, value) => currentState.set('localPrinterError', value);
const emvInteractionSuccess = (currentState, value) => currentState.set('paymentResult', { data: value, success: true });
const nakEmvInteractionEnd = (currentState, value) => currentState.set('nakTransactionResult', value);
const emvInteractionError = (currentState, value) => currentState.set('paymentResult', { data: value, success: false }).set('isLoadingCheckout', false);
const hideEmvCancel = (currentState) => currentState.set('showEmvCancel', false);
const cancelEmv = (currentState) => currentState.set('isLoadingCheckout', false).set('paymentResult', null).set('showEmvCancel', false);
const clearPaymentData = (currentState) => currentState.set('paymentResult', null);
const setLoadingCheckout = (currentState, value) => currentState.set('isLoadingCheckout', value);
const reducer = (currentState, action) => {
    switch (action.type) {
        case 12 /* Action.SET_LOADING_CHECKOUT */:
            return setLoadingCheckout(currentState, action.value);
        case 11 /* Action.CLEAR_PAYMENT_DATA */:
            return clearPaymentData(currentState);
        case 10 /* Action.CANCEL_EMV_CHECKOUT */:
            return cancelEmv(currentState);
        case 8 /* Action.TMDS_STATUS_CALL_TIMEOUT */:
        case 9 /* Action.CANCEL_EMV_CHECKOUT_SUCCESS */:
            return hideEmvCancel(currentState);
        case 0 /* Action.CLEAR_PRINTER_DATA */:
            return clearPrinterData(currentState);
        case 1 /* Action.CLEAR_TMDS_PRINTER_ERROR_NOTIFICATION */:
            return clearTmdsPrinterErrorNotification(currentState);
        case 6 /* Action.EMV_INTERACTION_ERROR */:
            return emvInteractionError(currentState, action.value);
        case 5 /* Action.EMV_INTERACTION_SUCCESS */:
            return emvInteractionSuccess(currentState, action.value);
        case 7 /* Action.NACK_EMV_CHECKOUT_END */:
            return nakEmvInteractionEnd(currentState, action.value);
        case 2 /* Action.SELECT_PRINTER */:
            return selectPrinter(currentState, action.value);
        case 3 /* Action.TMDS_PRINTER_ERROR_NOTIFICATION */:
            return tmdsPrinterErrorNotification(currentState, action.value);
        case 4 /* Action.TMDS_SET_DEVICES */:
            return tmdsSetDevices(currentState, action.value);
        default:
            return currentState;
    }
};
const dispatch = (action) => {
    state = reducer(getState(), action);
};
const getState = () => {
    if (!state) {
        state = toImmutableState(initialState);
    }
    return state;
};

const TMDS_EMV_APPROVED = 'APPROVED';
const isEmvTransactionApproved = (tmdsTransaction) => (tmdsTransaction === null || tmdsTransaction === void 0 ? void 0 : tmdsTransaction.response) === TMDS_EMV_APPROVED;

const handleTmdsPaymentStatus = (message) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    let status = "UNKNOWN" /* LogStatus.Unknown */;
    logger.debug(`Handle payment status for ${message.method}`);
    switch (message.method) {
        case TmdsMethod.StartTransaction: {
            const hasToken = !!((_b = (_a = message === null || message === void 0 ? void 0 : message.data) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.token);
            const isApproved = isEmvTransactionApproved((_c = message === null || message === void 0 ? void 0 : message.data) === null || _c === void 0 ? void 0 : _c.response);
            if (hasToken && isApproved) {
                logger.debug('Payment success');
                dispatch({ type: 5 /* Action.EMV_INTERACTION_SUCCESS */, value: Object.assign({}, (_d = message === null || message === void 0 ? void 0 : message.data) === null || _d === void 0 ? void 0 : _d.response) });
                status = "SUCCESS" /* LogStatus.Success */;
            }
            else {
                logger.debug('Payment error');
                dispatch({ type: 6 /* Action.EMV_INTERACTION_ERROR */, value: Object.assign({}, (_e = message === null || message === void 0 ? void 0 : message.data) === null || _e === void 0 ? void 0 : _e.response) });
                status = "ERROR" /* LogStatus.Error */;
            }
            break;
        }
        case TmdsMethod.StopTransaction: {
            dispatch({ type: 9 /* Action.CANCEL_EMV_CHECKOUT_SUCCESS */ });
            dispatch({
                type: 6 /* Action.EMV_INTERACTION_ERROR */,
                value: {
                    error: {
                        errorCode: '1004',
                    },
                },
            });
            status = "SUCCESS" /* LogStatus.Success */;
            break;
        }
        case TmdsMethod.NackTransaction: {
            if (((_g = (_f = message === null || message === void 0 ? void 0 : message.data) === null || _f === void 0 ? void 0 : _f.response) === null || _g === void 0 ? void 0 : _g.error.errorCode) == null) {
                dispatch({
                    type: 7 /* Action.NACK_EMV_CHECKOUT_END */,
                    value: {
                        success: true,
                    },
                });
            }
            else {
                dispatch({
                    type: 7 /* Action.NACK_EMV_CHECKOUT_END */,
                    value: {
                        success: false,
                        error: {
                            code: message.data.response.error.errorCode,
                            message: (_h = message.data.response.error.message) !== null && _h !== void 0 ? _h : undefined,
                        },
                    },
                });
            }
            status = "SUCCESS" /* LogStatus.Success */;
            break;
        }
        case TmdsMethod.AckTransaction:
        case TmdsMethod.TestConnectivity: {
            status = "INFO" /* LogStatus.Info */;
            break;
        }
        default:
            dispatch({ type: 6 /* Action.EMV_INTERACTION_ERROR */, value: Object.assign({}, message.data.response) });
            break;
    }
    logger.logClientToCloud({
        type: "EMV.Response" /* LogComponent.EmvResponse */,
        status,
        data: Object.assign({}, message),
    });
});

const errorToStatus = (err = '') => {
    return String(err).trim().replace(/\s/g, '_').toLowerCase();
};
const printerStatusNotify = (err = '') => {
    let title = tmdsMessages.unknownError;
    let suggestion = tmdsFragmentMessages.checkPrinter;
    const status = errorToStatus(err);
    switch (status) {
        case PrinterStatus.OnlineNotice:
            title = tmdsMessages.onlineNotice;
            break;
        case PrinterStatus.Offline:
        case PrinterStatus.NotAvailable:
        case PrinterStatus.OfflineNotice:
            title = tmdsMessages.offlineNotice;
            suggestion = tmdsFragmentMessages.suggestConnection;
            break;
        case PrinterStatus.Removed:
            title = tmdsMessages.removed;
            suggestion = tmdsFragmentMessages.checkPrinter;
            break;
        case PrinterStatus.Paused:
        case PrinterStatus.Stopped:
            title = tmdsMessages.stopped;
            suggestion = tmdsFragmentMessages.suggestConnection;
            break;
        case PrinterStatus.PaperOut:
        case PrinterStatus.OutOfPaper:
            title = tmdsMessages.outOfPaperError;
            suggestion = tmdsFragmentMessages.addPaper;
            break;
        case PrinterStatus.PaperJam:
            title = tmdsMessages.paperJamError;
            suggestion = tmdsFragmentMessages.removeJam;
            break;
        case PrinterStatus.PaperOutOrJam:
            title = tmdsMessages.paperOutOrJam;
            suggestion = tmdsFragmentMessages.addPaper;
            break;
        case PrinterStatus.PortNotExist:
        case PrinterStatus.PortAccessError:
            title = tmdsMessages.portAccessError;
            suggestion = tmdsFragmentMessages.checkPort;
            break;
        case PrinterStatus.CutterJam:
            title = tmdsMessages.cutterJam;
            suggestion = tmdsFragmentMessages.removeJam;
            break;
        case PrinterStatus.Done:
        case PrinterStatus.Error:
        case PrinterStatus.Ready:
        case PrinterStatus.Success:
        case PrinterStatus.Printing:
        case PrinterStatus.WrongRequestId:
    }
    return {
        body: tmdsMessages.extendedMessage,
        title,
        suggestion,
    };
};

const tokenForTmds = function* () {
    let count = 1000;
    while (true) {
        yield {
            requestid: `SDR-${count}`,
            token: v1(),
        };
        count += 1;
    }
};

const queueTmdsPrintingStatus = (token) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const { value: { requestid }, } = tokenForTmds().next();
        const message = generateTmdsMessage({
            requestid,
            resourceid: 'printDevice',
            method: TmdsMethod.GetPrintingStatus,
        });
        yield sendPostToTmds({ token, message });
    }
    catch (e) {
        logger.warn('Unable to get TMDS printing status', e);
    }
});

const sendResetPrinterToTmds = () => __awaiter(void 0, void 0, void 0, function* () {
    const { value: { requestid, token }, } = tokenForTmds().next();
    const printer = selectSelectedPrinterData(getState());
    const message = generateTmdsMessage({
        requestid,
        resourceid: 'printDevice',
        method: TmdsMethod.Reset,
        data: {
            device_id: (printer === null || printer === void 0 ? void 0 : printer.id) || '',
            timeout: 30,
        },
    });
    yield sendPostToTmds({ token, message });
    yield queueTmdsPrintingStatus(token);
});

const parseTmdsResponse = (response) => {
    var _a;
    if (!(response === null || response === void 0 ? void 0 : response.c)) {
        return null;
    }
    let ccpmessage = null;
    try {
        const parsedResults = JSON.parse(response.c);
        ccpmessage = (_a = parsedResults === null || parsedResults === void 0 ? void 0 : parsedResults.ccpmessage) !== null && _a !== void 0 ? _a : null;
    }
    catch (e) {
        logger.warn('Unable to parse TMDS status check response', response);
    }
    return ccpmessage;
};
const handleDeviceStatus = (message) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const devices = ((_a = message === null || message === void 0 ? void 0 : message.data) === null || _a === void 0 ? void 0 : _a.devices.device) || [];
    dispatch({ type: 4 /* Action.TMDS_SET_DEVICES */, value: devices });
    const selectedPrinter = selectSelectedPrinterData(getState());
    if (selectedPrinter) {
        const currentPrinters = selectPrinterList(getState());
        const matchingPrinter = currentPrinters.find((printer) => printer.id === selectedPrinter.id && printer.status !== selectedPrinter.status);
        if (matchingPrinter) {
            const status = String(matchingPrinter.status).trim().toLowerCase();
            if (status === PrinterStatus.Stopped) {
                yield sendResetPrinterToTmds();
            }
            else if (status !== PrinterStatus.Printing) {
                dispatch({ type: 1 /* Action.CLEAR_TMDS_PRINTER_ERROR_NOTIFICATION */ });
                const printerStatus = printerStatusNotify(PrinterStatus.OfflineNotice);
                dispatch({ type: 3 /* Action.TMDS_PRINTER_ERROR_NOTIFICATION */, value: printerStatus });
                TmdsClientConfiguration$1.printError(printerStatus);
            }
        }
    }
});
const handlePrintJobStatus = (message) => __awaiter(void 0, void 0, void 0, function* () {
    const status = String(message.data.status).trim().toLowerCase();
    let logStatus = "INFO" /* LogStatus.Info */;
    let isWrongRequestId = false;
    let errorMessage = '';
    try {
        errorMessage = String(message.data.error_message).trim().toLowerCase();
        isWrongRequestId = errorMessage === PrinterStatus.WrongRequestId;
    }
    catch (e) {
        logger.warn(e);
    }
    logger.debug('Print job status', status, errorMessage);
    let errorNotification = printerStatusNotify(status);
    if (JobCompleteStatus.indexOf(status) > -1) {
        // dispatch print success notification
        logStatus = "SUCCESS" /* LogStatus.Success */;
        errorMessage = null;
        errorNotification = null;
    }
    else if (!isWrongRequestId && status === PrinterStatus.Error) {
        dispatch({ type: 3 /* Action.TMDS_PRINTER_ERROR_NOTIFICATION */, value: errorNotification });
        TmdsClientConfiguration$1.printError(errorNotification);
        logStatus = "ERROR" /* LogStatus.Error */;
    }
    else if (/^(SUC|NONE)/i.test(message.data.error_message)) {
        // This accounts for a typo (SUCESS vs SUCCESS) and the status being returned as null.
        // dispatch print success notification ?
        logStatus = "SUCCESS" /* LogStatus.Success */;
        errorMessage = null;
        errorNotification = null;
    }
    else if (!isWrongRequestId) {
        // dispatch error
        dispatch({ type: 3 /* Action.TMDS_PRINTER_ERROR_NOTIFICATION */, value: errorNotification });
        TmdsClientConfiguration$1.printError(errorNotification);
        logStatus = "ERROR" /* LogStatus.Error */;
    }
    logger.logClientToCloud({
        type: "Print.Response" /* LogComponent.PrintResponse */,
        status: logStatus,
        data: {
            errorMessage,
            errorNotification,
            isWrongRequestId,
            status,
        },
    });
});
const handlePrint = (token, message) => __awaiter(void 0, void 0, void 0, function* () {
    const status = String(message.data.status).toLowerCase();
    logger.debug('Handle print status', status);
    if (status === PrinterStatus.Printing) {
        // print is in progress check again
        yield delay(2000);
        yield queueTmdsPrintingStatus(token);
    }
    else {
        yield handlePrintJobStatus(message);
    }
});
const handleTimeout = (response, action) => __awaiter(void 0, void 0, void 0, function* () {
    dispatch({ type: 8 /* Action.TMDS_STATUS_CALL_TIMEOUT */ });
    yield delay(2000);
    yield tmdsStatusCheck({ token: action.token, requestid: '', message: action.message });
});
const getStatusType = (tmdsMessage) => {
    var _a, _b;
    if ((_a = tmdsMessage === null || tmdsMessage === void 0 ? void 0 : tmdsMessage.ccpmessage) === null || _a === void 0 ? void 0 : _a.method) {
        const resourceId = ((_b = tmdsMessage === null || tmdsMessage === void 0 ? void 0 : tmdsMessage.ccpmessage) === null || _b === void 0 ? void 0 : _b.resourceid) || null;
        if (resourceId === 'paymentDevice') {
            return TmdsStatusCheckType.PAYMENT;
        }
        switch (tmdsMessage.ccpmessage.method) {
            case TmdsMethod.GetPrintingStatus:
            case TmdsMethod.Print:
                return TmdsStatusCheckType.PRINTING;
            case TmdsMethod.StartTransaction:
            case TmdsMethod.AckTransaction:
                return TmdsStatusCheckType.PAYMENT;
            case TmdsMethod.DeviceStatus:
            case TmdsMethod.Reset:
            case TmdsMethod.StopTransaction:
            case TmdsMethod.NackTransaction:
            case TmdsMethod.TestConnectivity:
            default:
                return TmdsStatusCheckType.GENERIC;
        }
    }
    return TmdsStatusCheckType.GENERIC;
};
var TmdsStatusCheckType;
(function (TmdsStatusCheckType) {
    TmdsStatusCheckType["GENERIC"] = "generic";
    TmdsStatusCheckType["PRINTING"] = "printing";
    TmdsStatusCheckType["PAYMENT"] = "payment";
})(TmdsStatusCheckType || (TmdsStatusCheckType = {}));
const handleTmdsStatusCheckError = (action) => __awaiter(void 0, void 0, void 0, function* () {
    if (action && action.message) {
        const tmdsMessage = JSON.parse(action.message);
        const statusType = getStatusType(tmdsMessage);
        logger.debug('Status', statusType);
        switch (statusType) {
            case TmdsStatusCheckType.PRINTING:
                yield queueTmdsPrintingStatus(action.token);
                break;
            case TmdsStatusCheckType.PAYMENT:
            case TmdsStatusCheckType.GENERIC:
        }
    }
});
const tmdsStatusCheck = ({ message, token, requestid, timeouts }) => __awaiter(void 0, void 0, void 0, function* () {
    const url = new URL(TMDS_DEFAULT_CHANNEL_URL);
    const action = {
        message,
        token,
        requestid,
        timeouts: timeouts ? timeouts + 1 : 0,
    };
    try {
        url.searchParams.append('_', (Math.random() * 2048).toString());
        url.searchParams.append('privateToken', token);
        url.searchParams.append('lastMessageId', token || '0');
        const response = yield fetchJsonp(url.toString(), { timeout: 60000 });
        const results = yield response.json();
        logger.debug('TMDS Status check response', results);
        const ccpmessage = parseTmdsResponse(results);
        logger.info('TMDS Status check message', ccpmessage);
        if (ccpmessage) {
            if (ccpmessage.resourceid === 'paymentDevice') {
                logger.debug('TMDS status check: handle payment');
                yield handleTmdsPaymentStatus(ccpmessage);
            }
            if (ccpmessage.method === TmdsMethod.DeviceStatus) {
                logger.debug('TMDS status check: device status');
                yield handleDeviceStatus(ccpmessage);
            }
            else if (ccpmessage.method === TmdsMethod.GetPrintingStatus || ccpmessage.method === TmdsMethod.Print) {
                logger.debug('TMDS status check: handle print');
                yield handlePrint(token, ccpmessage);
            }
        }
        else if (results.n && /timeout/i.test(results.n) && action.timeouts < 5) {
            logger.debug('TMDS status check: timeout');
            yield handleTimeout(results, action);
        }
        else {
            logger.debug('Unhandled TMDS response', results);
        }
    }
    catch (e) {
        logger.warn('Unable to get TMDS status', e, action);
        yield delay(2000);
        logger.debug('TMDS status check: error');
        yield handleTmdsStatusCheckError(action);
    }
});

const fetchJson = (input, init) => __awaiter(void 0, void 0, void 0, function* () {
    return fetch(input, init).then((response) => {
        if (response.type !== 'opaque' && response.type !== 'opaqueredirect') {
            if (!response.ok) {
                throw new Error(response.statusText);
            }
        }
        return response.body ? response.json() : Promise.resolve();
    });
});

const sendPostToTmds = ({ token, message }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const options = buildTmdsRequest(token, message);
        yield fetchJson(TMDS_SEND_MESSAGE_URL, options);
        yield delay(250);
        yield tmdsStatusCheck({ token, message, requestid: '' });
    }
    catch (e) {
        logger.warn('Unable to send message to TMDS', e);
    }
});
const buildTmdsRequest = (token, message) => {
    const formData = new FormData();
    formData.append('token', token);
    formData.append('message', urlencode(message));
    // It should not be required to cast formData to never
    // but there's an issue in TypeScript: https://github.com/microsoft/TypeScript/issues/19806
    const body = new URLSearchParams(formData);
    return {
        method: 'POST',
        mode: 'no-cors',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
    };
};

const sendEmvTransactionToTmds = ({ method, data }) => __awaiter(void 0, void 0, void 0, function* () {
    const { value: { requestid, token }, } = tokenForTmds().next();
    const message = generateTmdsMessage({
        requestid,
        resourceid: 'paymentDevice',
        method,
        data: Object.assign({ deviceid: 'paymentDevice', timeout: 120 }, data),
    });
    logger.logClientToCloud({
        type: "EMV.Request" /* LogComponent.EmvRequest */,
        status: "INFO" /* LogStatus.Info */,
        data: {
            method,
            message: data,
        },
    });
    yield sendPostToTmds({ token, message });
});
var TransactionType;
(function (TransactionType) {
    TransactionType["Sale"] = "S";
    TransactionType["Refund"] = "C";
    TransactionType["Void"] = "V";
    TransactionType["TokenExchange"] = "X";
    TransactionType["TokenOnly"] = "T";
})(TransactionType || (TransactionType = {}));
var MopType;
(function (MopType) {
    MopType["Visa"] = "VI";
    MopType["Mastercard"] = "MC";
    MopType["Amex"] = "AX";
    MopType["Discover"] = "DI";
    MopType["JCB"] = "JC";
    MopType["Diners"] = "ED";
    MopType["PrivateLabel"] = "PL";
    MopType["Debit"] = "DE";
    MopType["GiftCard"] = "GC";
    MopType["Insignia"] = "INSIGNIA";
})(MopType || (MopType = {}));
const startEmvTransaction = (data) => __awaiter(void 0, void 0, void 0, function* () {
    yield sendEmvTransactionToTmds({ method: TmdsMethod.StartTransaction, data });
});
const stopEmvTransaction = () => __awaiter(void 0, void 0, void 0, function* () {
    yield sendEmvTransactionToTmds({ method: TmdsMethod.StopTransaction });
});
const acknowledgeEmvTransaction = () => __awaiter(void 0, void 0, void 0, function* () {
    if (hasPaymentDeviceAndPendingTransaction()) {
        yield sendEmvTransactionToTmds({ method: TmdsMethod.AckTransaction });
    }
});
const reverseEmvTransaction = () => __awaiter(void 0, void 0, void 0, function* () {
    if (hasPaymentDeviceAndPendingTransaction()) {
        yield sendEmvTransactionToTmds({ method: TmdsMethod.NackTransaction });
    }
});
const hasPaymentDeviceAndPendingTransaction = () => { var _a; return selectHasPaymentDevice(getState()) && ((_a = selectPaymentResult(getState())) === null || _a === void 0 ? void 0 : _a.data); };

const getTmdsDeviceDetails = () => __awaiter(void 0, void 0, void 0, function* () {
    const { value: { requestid, token }, } = tokenForTmds().next();
    const message = generateTmdsMessage({
        requestid,
        method: TmdsMethod.DeviceStatus,
        resourceid: 'hardwareservice',
        data: {
            connectionType: '',
            hardwareType: 'printDevice',
        },
    });
    yield sendPostToTmds({ token, message });
});

const sendPrintToTmds = (printer) => (files) => __awaiter(void 0, void 0, void 0, function* () {
    for (const file of files) {
        const { value: { requestid, token }, } = tokenForTmds().next();
        const message = generateTmdsMessage({
            requestid,
            resourceid: 'printDevice',
            method: TmdsMethod.Print,
            data: {
                device_id: printer.id,
                timeout: 30,
                format: printer.method === PrintingMethod.BOCA || printer.method === PrintingMethod.FGL
                    ? PrintingMethod.FGL
                    : PrintingMethod.PDF,
                encoding: 'BASE64',
                compression: 'None',
                data: file,
            },
        });
        try {
            yield sendPostToTmds({ token, message });
        }
        catch (e) {
            logger.warn('Unable to print tickets', e);
        }
    }
});
const print = (files) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        if (!(files === null || files === void 0 ? void 0 : files.length)) {
            return;
        }
        const printer = selectSelectedPrinterData(getState());
        logger.debug('Selected printer to print', printer);
        if (!printer) {
            return;
        }
        logger.logClientToCloud({
            type: "Print.Request" /* LogComponent.PrintRequest */,
            status: "INFO" /* LogStatus.Info */,
            data: {
                filesCount: files.length,
                printer,
            },
        });
        yield sendPrintToTmds(printer)(files);
    }
    catch (e) {
        logger.warn('Unable to print tickets', e);
    }
});

const instance = Symbol();
class TmdsClient {
    constructor() {
        this.clearPrinterErrorNotification = () => {
            dispatch({ type: 1 /* Action.CLEAR_TMDS_PRINTER_ERROR_NOTIFICATION */ });
        };
        this.clearPrinterData = () => {
            dispatch({ type: 0 /* Action.CLEAR_PRINTER_DATA */ });
        };
        this.clearPaymentData = () => {
            dispatch({ type: 11 /* Action.CLEAR_PAYMENT_DATA */ });
        };
        this.selectPrinter = (printer) => {
            dispatch({ type: 2 /* Action.SELECT_PRINTER */, value: printer });
        };
        this.setLoadingCheckout = (isLoading) => {
            dispatch({ type: 12 /* Action.SET_LOADING_CHECKOUT */, value: isLoading });
        };
        this.getTmdsDeviceDetails = () => __awaiter(this, void 0, void 0, function* () {
            return getTmdsDeviceDetails();
        });
        this.tmdsStatusCheck = (data) => __awaiter(this, void 0, void 0, function* () {
            return tmdsStatusCheck(data);
        });
        this.print = (files) => __awaiter(this, void 0, void 0, function* () {
            return print(files);
        });
        this.processEmvTransaction = (data) => __awaiter(this, void 0, void 0, function* () {
            yield startEmvTransaction(data);
            return selectPaymentResult(getState());
        });
        this.stopEmvTransaction = () => __awaiter(this, void 0, void 0, function* () {
            yield stopEmvTransaction();
        });
        this.acknowledgeEmvTransaction = () => __awaiter(this, void 0, void 0, function* () {
            yield acknowledgeEmvTransaction();
        });
        this.reverseEmvTransaction = () => __awaiter(this, void 0, void 0, function* () {
            yield reverseEmvTransaction();
            return selectNakTransactionResult(getState());
        });
        this.configure = (options) => {
            TmdsClientConfiguration$1.applyGlobalConfiguration(options);
        };
        this.printerStatusNotify = (error) => {
            return printerStatusNotify(error);
        };
    }
    static get instance() {
        if (!TmdsClient[instance]) {
            TmdsClient[instance] = new TmdsClient();
        }
        return TmdsClient[instance];
    }
    get hasPaymentDevice() {
        return selectHasPaymentDevice(getState());
    }
    get selectedPrinter() {
        return selectSelectedPrinter(getState());
    }
    get selectedPrinterData() {
        return selectSelectedPrinterData(getState());
    }
    get printerList() {
        return selectPrinterList(getState());
    }
    get localPrinterError() {
        return selectLocalPrinterError(getState());
    }
    get showEmvCancel() {
        return selectShowEmvCancel(getState());
    }
    get isLoadingCheckout() {
        return selectIsLoadingCheckout(getState());
    }
}
var TmdsClientInstance = TmdsClient.instance;

export { MopType, PrintingDpi, PrintingMethod, TmdsMethod, TransactionType, TmdsClientInstance as default, tmdsFragmentMessages, tmdsMessages };
//# sourceMappingURL=index.es.js.map
